import { getApiEndpoints, getApiEnvironment } from "./config";
import { GlossaryEntry } from "./mockData";
import { ModelConfig } from "./translationService";
import { StoredSegmentState } from "./segmentSync";
import { DocumentStateRecord, fetchAllDocumentStates, fetchWorkspaceSettings } from "./translationStateApi";
export interface WorkspaceBackup {
  version: "2.0";
  exportedAt: string;
  backupType: "manual" | "pre-bulk-approve" | "pre-reimport-fr";
  settings: {
    apiKey: string;
    models: ModelConfig[];
    apiEnvironment: string;
    customEndpoints: {
      lawvoApi: string;
      dcApi: string;
    };
  };
  glossary: GlossaryEntry[];
  segmentStates: Record<string, StoredSegmentState[]>;
  segmentOverrides: Record<string, {
    status: string;
    mergedText?: string;
    sourceText?: string;
  }>;
  documentStateRecords?: DocumentStateRecord[];
  stats: {
    documentCount: number;
    segmentCount: number;
    approvedCount: number;
  };
}
function segmentStatesFromRecords(records: DocumentStateRecord[]): Record<string, StoredSegmentState[]> {
  const segmentStates: Record<string, StoredSegmentState[]> = {};
  for (const record of records) {
    segmentStates[record.documentPairKey] = record.segments || [];
  }
  return segmentStates;
}
function legacyOverridesFromStates(segmentStates: Record<string, StoredSegmentState[]>): WorkspaceBackup["segmentOverrides"] {
  const overrides: WorkspaceBackup["segmentOverrides"] = {};
  for (const [docId, states] of Object.entries(segmentStates)) {
    for (const state of states) {
      overrides[`${docId}-${state.sectionId}`] = {
        status: state.status,
        mergedText: state.mergedText,
        sourceText: state.sourceText
      };
    }
  }
  return overrides;
}
function computeStats(segmentStates: Record<string, StoredSegmentState[]>) {
  let segmentCount = 0;
  let approvedCount = 0;
  for (const states of Object.values(segmentStates)) {
    segmentCount += states.length;
    approvedCount += states.filter(s => s.status === "approved").length;
  }
  return {
    documentCount: Object.keys(segmentStates).length,
    segmentCount,
    approvedCount
  };
}
export async function fetchServerWorkspaceBackup(options?: {
  backupType?: WorkspaceBackup["backupType"];
  apiKey?: string;
  models?: ModelConfig[];
  glossary?: GlossaryEntry[];
}): Promise<WorkspaceBackup> {
  const [workspaceSettings, documentStateRecords] = await Promise.all([fetchWorkspaceSettings().catch(() => null), fetchAllDocumentStates().catch(() => [] as DocumentStateRecord[])]);
  const segmentStates = segmentStatesFromRecords(documentStateRecords);
  const endpoints = getApiEndpoints();
  return {
    version: "2.0",
    exportedAt: new Date().toISOString(),
    backupType: options?.backupType || "manual",
    settings: {
      apiKey: options?.apiKey || workspaceSettings?.openRouterApiKey || "",
      models: options?.models || workspaceSettings?.modelConfigs || [],
      apiEnvironment: getApiEnvironment(),
      customEndpoints: {
        lawvoApi: endpoints.lawvoApi,
        dcApi: endpoints.dcApi
      }
    },
    glossary: options?.glossary || workspaceSettings?.glossary || [],
    segmentStates,
    segmentOverrides: legacyOverridesFromStates(segmentStates),
    documentStateRecords,
    stats: computeStats(segmentStates)
  };
}
export function downloadWorkspaceBackup(backup: WorkspaceBackup, filenamePrefix = "lawvo-backup"): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const suffix = backup.backupType === "pre-bulk-approve" || backup.backupType === "pre-reimport-fr" ? backup.backupType : "export";
  const filename = `${filenamePrefix}-${suffix}-${stamp}.json`;
  const blob = new Blob([JSON.stringify(backup, null, 2)], {
    type: "application/json"
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
  return filename;
}